import { Navigate, Route, Routes } from 'react-router-dom'

import { OwnerLayout } from '../consoles/owner/OwnerLayout'
import { BackupScreen } from '../consoles/owner/screens/BackupScreen'
import { HistoryScreen } from '../consoles/owner/screens/HistoryScreen'
import { NewPersonScreen } from '../consoles/owner/screens/NewPersonScreen'
import { OwnerOverviewScreen } from '../consoles/owner/screens/OwnerOverviewScreen'
import { OwnerSettingsScreen } from '../consoles/owner/screens/OwnerSettingsScreen'
import { PeopleScreen } from '../consoles/owner/screens/PeopleScreen'
import { PlatformLayout } from '../consoles/platform/PlatformLayout'
import { AccessLogScreen } from '../consoles/platform/screens/AccessLogScreen'
import { BusinessDetailScreen } from '../consoles/platform/screens/BusinessDetailScreen'
import { BusinessesScreen } from '../consoles/platform/screens/BusinessesScreen'
import { ConsoleSettingsScreen } from '../consoles/platform/screens/ConsoleSettingsScreen'
import { HealthScreen } from '../consoles/platform/screens/HealthScreen'
import { InsideBusinessScreen } from '../consoles/platform/screens/InsideBusinessScreen'
import { OverviewScreen } from '../consoles/platform/screens/OverviewScreen'
import { PlansScreen } from '../consoles/platform/screens/PlansScreen'
import { SubscriptionsScreen } from '../consoles/platform/screens/SubscriptionsScreen'
import { SupportScreen } from '../consoles/platform/screens/SupportScreen'
import { UsageScreen } from '../consoles/platform/screens/UsageScreen'

import { LoadingSkeleton } from '../components'
import { t } from '../lib/i18n'

import { SignInScreen } from './SignInScreen'
import { useIsPlatformOwner, useSessionState } from './session'

/**
 * A route a role cannot use is ABSENT — not present-and-refused. `/plans` does
 * not exist for a support account: typing the URL lands on the overview rather
 * than on a permission notice, because a notice is still a disclosure that the
 * screen exists.
 *
 * This still holds against the real API: `useIsPlatformOwner` now reads the role
 * the SERVER issued at sign-in, not a `?as=` query parameter, and the server
 * refuses `/platform/plans` independently for anyone who reaches it another way.
 */
function OwnerOnly({ children }: { children: React.ReactNode }) {
  return useIsPlatformOwner() ? <>{children}</> : <Navigate to="/" replace />
}

/**
 * Nobody signed in gets the sign-in form for the console they asked for, not a
 * redirect to a single shared one: which door you use decides which endpoint
 * authenticates you, and the two are deliberately not the same door.
 */
function Authenticated({ children }: { children: React.ReactNode }) {
  const { session, isPending, surface } = useSessionState()

  if (isPending) {
    return (
      <div className="p-7" aria-label={t('auth.checking')}>
        <LoadingSkeleton variant="tiles" />
      </div>
    )
  }

  if (!session) return <SignInScreen surface={surface} />

  return <>{children}</>
}

export function AppRoutes() {
  return (
    <Routes>
      {/* The trader's own console — accent blue. Declared first so `/admin`
          never falls through to the platform console's `:slug` routes. */}
      <Route
        path="/admin"
        element={
          <Authenticated>
            <OwnerLayout />
          </Authenticated>
        }
      >
        <Route index element={<OwnerOverviewScreen />} />
        <Route path="people" element={<PeopleScreen />} />
        <Route path="people/new" element={<NewPersonScreen />} />
        <Route path="history" element={<HistoryScreen />} />
        <Route path="backup" element={<BackupScreen />} />
        <Route path="settings" element={<OwnerSettingsScreen />} />
      </Route>

      {/* The Platform Console — accent indigo. */}
      <Route
        path="/"
        element={
          <Authenticated>
            <PlatformLayout />
          </Authenticated>
        }
      >
        <Route index element={<OverviewScreen />} />
        <Route path="businesses" element={<BusinessesScreen />} />
        <Route path="businesses/:slug" element={<BusinessDetailScreen />} />
        <Route path="businesses/:slug/book/*" element={<InsideBusinessScreen />} />
        <Route
          path="plans"
          element={
            <OwnerOnly>
              <PlansScreen />
            </OwnerOnly>
          }
        />
        <Route path="subscriptions" element={<SubscriptionsScreen />} />
        <Route path="usage" element={<UsageScreen />} />
        <Route path="health" element={<HealthScreen />} />
        <Route path="support" element={<SupportScreen />} />
        <Route path="access-log" element={<AccessLogScreen />} />
        <Route path="settings" element={<ConsoleSettingsScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
